<script setup lang="ts">
  import {
    PosDevicesService,
    ICreatePosDeviceDto,
    IUpdatePosDeviceDto,
  } from '@/api/services/pos-devices.service';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { IPosDevice } from '@/interfaces/IPosDevice';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { computed, getCurrentInstance, onMounted, ref } from 'vue';
  import { AlertType } from '@/interfaces/IAlert';

  const props = defineProps<{
    posDeviceToEdit?: IPosDevice;
  }>();

  const restaurantStore = useRestaurantStore();

  // Initialize with first branch as default for new devices
  const defaultBranchId = computed(() => {
    const branches = restaurantStore.restaurant.branches || [];
    return branches.length > 0 ? branches[0].id : undefined;
  });

  const deviceData = ref<ICreatePosDeviceDto | IUpdatePosDeviceDto>({
    name: '',
    branchId: defaultBranchId.value,
  });

  onMounted(() => {
    if (props.posDeviceToEdit) {
      deviceData.value = {
        name: props.posDeviceToEdit.name,
        branchId: props.posDeviceToEdit.branchId, // Display only (field is disabled)
        enabled: props.posDeviceToEdit.enabled,
      } as IUpdatePosDeviceDto;
    }
  });
  const ui = useUi();
  const valid = ref(false);
  const modals = useModal();
  const { proxy } = getCurrentInstance();

  const isEditMode = computed(() => !!props.posDeviceToEdit);
  const title = computed(() =>
    isEditMode.value ? 'Editar Dispositivo POS' : 'Nuevo Dispositivo POS'
  );

  const branches = computed(() => restaurantStore.restaurant.branches || []);

  const saving = ref(false);
  async function save() {
    if (!valid.value || saving.value) return;

    try {
      saving.value = true;

      let posDevice: IPosDevice;
      if (!isEditMode.value) {
        posDevice = await PosDevicesService.create(
          restaurantStore.restaurant.id,
          deviceData.value as ICreatePosDeviceDto
        );
        ui.showToast(AlertType.Success, 'Dispositivo creado exitosamente');
      } else {
        // Exclude branchId from update payload (it's immutable and only used for display)
        const {
          branchId,
          ...updatePayload
        } = deviceData.value as IUpdatePosDeviceDto;
        posDevice = await PosDevicesService.update(
          restaurantStore.restaurant.id,
          props.posDeviceToEdit.id,
          updatePayload
        );
        ui.showToast(AlertType.Success, 'Dispositivo actualizado exitosamente');
      }

      saving.value = false;
      proxy.$parent.$emit('saved', posDevice);
      close();
    } catch (error) {
      ui.serverError(error, 'Error al guardar el dispositivo');
      saving.value = false;
    }
  }

  const close = () => {
    modals.hide('pos-device');
  };
</script>

<template>
  <div class="modals-pos-device-container">
    <v-card>
      <v-card-title>
        <span>{{ title }}</span>
        <v-spacer />
        <v-btn icon small @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-form v-model="valid" ref="form">
          <v-text-field
            v-model="deviceData.name"
            label="Nombre del dispositivo *"
            :rules="[(v) => !!v || 'El nombre es requerido']"
            outlined
            dense
            prepend-inner-icon="mdi-tablet"
            hint="Ejemplo: Caja Principal, POS 1, Tablet Mesero"
            persistent-hint
          />

          <v-select
            v-model="deviceData.branchId"
            :items="branches"
            item-text="name"
            item-value="id"
            label="Sucursal *"
            :rules="[(v) => !!v || 'La sucursal es requerida']"
            :disabled="isEditMode"
            outlined
            dense
            prepend-inner-icon="mdi-store"
            :hint="
              isEditMode
                ? 'La sucursal no puede cambiarse después de crear el dispositivo'
                : 'Seleccione la sucursal a la que pertenece este dispositivo'
            "
            persistent-hint
          />

          <v-switch
            v-if="isEditMode"
            v-model="deviceData.enabled"
            label="Dispositivo habilitado"
            color="success"
            hint="Deshabilitado: El dispositivo no podrá autenticarse"
            persistent-hint
          />

          <div v-if="isEditMode && posDeviceToEdit" class="device-info">
            <v-divider class="tw-my-4" />
            <div class="info-section">
              <h3 class="tw-text-sm tw-font-medium tw-mb-2">
                Información del dispositivo
              </h3>
              <div class="info-row">
                <span class="label">Token:</span>
                <code class="value">{{ posDeviceToEdit.token }}</code>
              </div>
              <div class="info-row">
                <span class="label">Estado:</span>
                <v-chip
                  x-small
                  :color="posDeviceToEdit.deviceId ? 'success' : 'warning'"
                >
                  {{ posDeviceToEdit.deviceId ? 'Vinculado' : 'No vinculado' }}
                </v-chip>
              </div>
              <div v-if="posDeviceToEdit.humanIdPrefix" class="info-row">
                <span class="label">Prefijo Take Away:</span>
                <code class="value">{{ posDeviceToEdit.humanIdPrefix }}</code>
              </div>
              <div v-if="posDeviceToEdit.lastLoginAt" class="info-row">
                <span class="label">Último acceso:</span>
                <span class="value">
                  {{
                    new Date(posDeviceToEdit.lastLoginAt).toLocaleString(
                      'es-ES'
                    )
                  }}
                </span>
              </div>
            </div>
          </div>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close" :disabled="saving">Cancelar</v-btn>
        <v-btn
          color="success"
          @click="save"
          :disabled="!valid"
          :loading="saving"
        >
          {{ isEditMode ? 'Guardar' : 'Crear' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<style scoped lang="scss">
  .modals-pos-device-container {
    @apply tw-items-stretch tw-flex-col;

    .device-info {
      .info-section {
        @apply tw-bg-gray-50 tw-p-3 tw-rounded;

        .info-row {
          @apply tw-flex tw-items-center tw-gap-2 tw-mb-2 tw-text-sm;

          &:last-child {
            @apply tw-mb-0;
          }

          .label {
            @apply tw-font-medium tw-text-gray-600 tw-min-w-[120px];
          }

          .value {
            @apply tw-text-gray-900;
          }

          code.value {
            @apply tw-bg-white tw-px-2 tw-py-1 tw-rounded tw-font-mono tw-text-xs;
          }
        }
      }
    }
  }
</style>
